/* eslint-disable jsx-a11y/anchor-is-valid */
import { motion, useTransform, useScroll } from 'framer-motion'
import Head from 'next/head'
import React, { useState } from 'react'
import { IconContext } from 'react-icons'
import { IoChevronBackSharp, IoChevronForwardSharp } from 'react-icons/io5'
import styled, { css } from 'styled-components'
import { MediaUploadFieldsFragment, MediaUploadTransformationStatus, MediaUploadType } from 'types/graphql'
import { config } from 'utils/config'
import { resizedImageUrl } from 'utils/imageUrl'
import { media } from 'utils/media'
import { Button } from 'lib/Button/Button'
import { GalleryPopup } from 'lib/GalleryPopup'
import { Text } from 'lib/Text'
import { useCurrentBrand } from './context/CurrentBrandProvider'

const Container = styled.figure`
  margin-bottom: 32px;
  ${media.tabletDown} {
    margin-bottom: 8px;
    margin-top: 0;
    margin-left: calc(var(--grid-gap-large) * -1);
    margin-right: calc(var(--grid-gap-large) * -1);
  }
  ${media.phoneDown} {
    margin-left: calc(var(--grid-gap-small) * -1);
    margin-right: calc(var(--grid-gap-small) * -1);
  }
`

const ImageContainer = styled.div`
  border: var(--border);
  overflow: hidden;
  position: relative;
  padding-top: 100%; // Hack to make the container square before the image is loaded
  ${media.tabletDown} {
    border: 0;
  }
`

const Image = styled(motion.img)<{ $rip?: boolean }>`
  height: 100%;
  width: 100%;
  object-fit: cover;
  position: absolute;
  top: 0;
  left: 0;
  transition: filter 0.2s;

  ${(props) =>
    props.$rip &&
    css`
      filter: grayscale(100%);
    `}

  ${(props) =>
    !props.$rip &&
    css`
      &.lazyloading {
        filter: brightness(0.9);
      }

      &.lazyloaded {
        filter: brightness(1);
      }
    `}
`

const ImageCredit = styled(Text)`
  margin-top: 12px;
  text-align: center;
`

const SecondaryImages = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 32px;
  margin-top: -24px;
  ${media.tabletDown} {
    margin-bottom: 16px;
    margin-top: 0;
    padding: 0 8px;
  }
`

const ImageThumbnail = styled.button<{ active?: boolean; isPlaceholder?: boolean }>`
  background: none;
  border: 2px solid var(--neutral-10);
  cursor: pointer;

  justify-content: center;
  align-items: center;
  display: flex;
  position: relative;
  transition: border-color 0.15s;
  max-width: 80px;
  max-height: 80px;
  aspect-ratio: 1 / 1;
  flex: 1 1 20%;
  overflow: hidden;

  &:hover {
    border-color: var(--neutral-20);
  }
  ${({ active }) =>
    active &&
    css`
      border: 2px solid black !important;
    `}

  ${({ isPlaceholder }) =>
    isPlaceholder &&
    css`
      background: var(--neutral-10);
      cursor: auto;
      &:hover {
        border: 2px solid var(--neutral-10);
      }
    `}
`

const Overlay = styled.div`
  align-items: center;
  background: rgba(0, 0, 0, 0.4);
  bottom: 0;
  display: flex;
  justify-content: space-between;
  left: 0;
  opacity: 0;
  padding: 24px;
  position: absolute;
  z-index: 1;
  right: 0;
  top: 0;
  transition: opacity 0.2s 0.1s;
  &:hover,
  &:active {
    opacity: 1;
  }
  ${media.phoneDown} {
    padding: 8px;
  }
`

const ArrowButton = styled(Button)`
  height: 48px;
  width: 48px;
  padding: 0;

  // Optically center the arrows
  &:first-of-type {
    padding: 0 2px 0 0;
  }
  &:last-of-type {
    padding: 0 0 0 2px;
  }
`

const IMAGE_SIZE = 567
const THUMBNAIL_SIZE = 80

export const BrandImages = () => {
  const { brand } = useCurrentBrand()
  const { scrollY } = useScroll()

  const [selectedImage, setSelectedImage] = useState<number | null>(null)
  const [previewImage, setPreviewImage] = useState<number>(0)

  const hasPrimaryImage = !!brand.primaryImageFullPath
  const hasSecondaryImages = brand.secondaryImages.length > 0

  const primaryImage = hasPrimaryImage
    ? {
        id: 'primary',
        caption: brand.title,
        credit: brand.primaryImageCredit || brand.title,
        filePath: brand.primaryImageFullPath,
        type: MediaUploadType.Image,
        transformationStatus: MediaUploadTransformationStatus.Ready
      }
    : null

  const images = [primaryImage, ...brand.secondaryImages].filter((image): image is MediaUploadFieldsFragment => !!image)

  const handleModalClose = () => {
    setSelectedImage(null)
  }

  const handleArrowClick = (direction: 'next' | 'prev') => {
    const addOrRemove = direction === 'next' ? 1 : previewImage === 0 ? images.length - 1 : -1
    const newIndex = (previewImage + addOrRemove) % images.length
    setPreviewImage(newIndex)
  }

  const handleThumbnailClick = (newIndex: number) => {
    if (newIndex === previewImage) {
      setSelectedImage(newIndex)
    } else {
      setPreviewImage(newIndex)
    }
  }

  // Bounding the scale transform to scale the image from 1x to 1.2x when the viewport scroll goes between 0 and 600px
  const scale = useTransform(scrollY, [0, 680], [1, 1.2])

  const urlsForImage = (image: MediaUploadFieldsFragment) => ({
    imageSizeLoading: resizedImageUrl(image.filePath, { quality: 20, blur: 1, sharpen: 0, width: IMAGE_SIZE, height: IMAGE_SIZE }),
    imageUrl1x: resizedImageUrl(image.filePath, { width: IMAGE_SIZE, height: IMAGE_SIZE }),
    imageUrl2x: resizedImageUrl(image.filePath, { width: IMAGE_SIZE * 2, height: IMAGE_SIZE * 2 }),
    thumbnail1x: resizedImageUrl(image.filePath, { width: THUMBNAIL_SIZE, height: THUMBNAIL_SIZE }),
    thumbnail2x: resizedImageUrl(image.filePath, { width: THUMBNAIL_SIZE * 2, height: THUMBNAIL_SIZE * 2 })
  })

  /**
   * Adding the `key` below fixes the bug where a brand image is not immediately being replaced when
   * navigating to a new page
   */
  return (
    <React.Fragment key={brand.id}>
      <Head>
        {images.map((image) => (
          <link key={image.id} rel="preload" href={urlsForImage(image).imageSizeLoading} as="image" />
        ))}
      </Head>

      <>
        {images[previewImage] && (
          <Container>
            <ImageContainer>
              <Image
                height={IMAGE_SIZE}
                width={IMAGE_SIZE}
                src={urlsForImage(images[previewImage]).imageSizeLoading}
                data-src={urlsForImage(images[previewImage]).imageUrl1x}
                data-srcset={`${urlsForImage(images[previewImage]).imageUrl1x} 1x, ${urlsForImage(images[previewImage]).imageUrl2x} 2x`}
                style={{ scale }}
                className="lazyload"
                $rip={brand.rip}
                alt={`${brand.title} Reviews`}
              />
              {config.multipleImagesEnabled && hasSecondaryImages && (
                <Overlay>
                  <IconContext.Provider value={{ style: { width: 32 } }}>
                    <ArrowButton onClick={() => handleArrowClick('prev')} round color="primary" label={<IoChevronBackSharp size={32} />} />
                    <Button onClick={() => setSelectedImage(previewImage)} round color="primary" label="View gallery" />
                    <ArrowButton onClick={() => handleArrowClick('next')} round color="primary" label={<IoChevronForwardSharp size={32} />} />
                  </IconContext.Provider>
                </Overlay>
              )}
            </ImageContainer>
            <ImageCredit weight="regular" size="small" color="neutral-60" as="figcaption">
              {images[previewImage].credit ? <>Image credit: {images[previewImage].credit}</> : <>&nbsp;</>}
            </ImageCredit>
          </Container>
        )}

        {config.multipleImagesEnabled && hasSecondaryImages && (
          <>
            <SecondaryImages>
              {images.slice(0, images.length > 5 ? 4 : 5).map((image, ix) => (
                <ImageThumbnail active={ix === previewImage} key={image.id} onClick={() => handleThumbnailClick(ix)}>
                  <Image
                    className="lazyload"
                    height={THUMBNAIL_SIZE}
                    width={THUMBNAIL_SIZE}
                    src={urlsForImage(image).thumbnail1x}
                    data-src={urlsForImage(image).thumbnail1x}
                    data-srcset={`${urlsForImage(image).thumbnail1x} 1x, ${urlsForImage(image).thumbnail2x} 2x`}
                  />
                </ImageThumbnail>
              ))}

              {images.length > 5 && (
                <ImageThumbnail active={previewImage >= 4} onClick={() => setSelectedImage(5)}>
                  <Text weight="semi" size="p">
                    +{images.length - 4}
                  </Text>
                </ImageThumbnail>
              )}

              {images.length < 5 &&
                new Array(5 - images.length).fill(0).map((_, ix) => <ImageThumbnail key={`placeholder-${ix}`} disabled isPlaceholder />)}
            </SecondaryImages>
            <GalleryPopup
              images={images}
              index={selectedImage}
              mobileFullscreen={false}
              onRequestClose={handleModalClose}
              open={selectedImage != null}
              showBorder={false}
              title={null}
            />
          </>
        )}
      </>
    </React.Fragment>
  )
}
